// 효과음 목록과 소리별 기본 음량·동시 재생 묶음. 파일은 public/assets/audio/sfx/<이름>.mp3(tools/audio/manifest.json).

export const SFX_NAMES = [
  'swing0', 'swing1', 'swing2', 'swing3', 'swingSkill',
  'hit0', 'hit1', 'hit2', 'hitCrit', 'hitFinisher', 'hitSkill', 'hitLight', 'hitBoss', 'blocked', 'heroHurt',
  'kill', 'killBig', 'massKill',
  'praise1', 'praise2', 'praise3', 'praise4',
  'skill1', 'skill2', 'skill3', 'skill3Land', 'dodge',
  'awakenGather', 'awakenFlash', 'awakenSlash',
  'mobAttack', 'mobSpawn', 'teleFire', 'bossIntro', 'bossDefeated',
  'waveStart', 'roomClear', 'gateOpen', 'gateClose', 'stageClear', 'defeat',
  'uiClick', 'uiConfirm', 'cardPick',
  'ventWarn', 'ventErupt', 'lavaBurn', 'shamanCast', 'shamanMortar',
  'emberIntro', 'emberNova', 'emberMeteor', 'emberFissure', 'emberSummon', 'emberDefeated',
  'r2Ambience', 'villageGateEmber',
  'linkFinish',
  // 2026-10-20 스킬 트리(docs/SKILL-TREE.md 8절): 새 스킬·랭크5/캡스톤 추가 효과·레벨업·배우기/초기화
  'skill4Cast', 'skill4Wave', 'burstWake', 'burstStormEye', 'burstAftershock', 'burstBloom', 'burstThunder',
  'levelUp', 'skillLearn', 'skillReset',
] as const;
export type SfxName = (typeof SFX_NAMES)[number];

/** 이름 → 번호(재생 경로는 번호만 쓴다) */
export const S = Object.fromEntries(SFX_NAMES.map((n, i) => [n, i])) as { readonly [K in SfxName]: number };

/** 동시 재생 제한 묶음 */
export const Group = { Swing: 0, Hit: 1, Kill: 2, Spawn: 3, MobAttack: 4, Hurt: 5, Big: 6, Ui: 7, Misc: 8, Layer: 9 } as const;
/** 묶음별: window초 안에 최대 max개 시작 */
export const GROUP_LIMITS: readonly { max: number; window: number }[] = [
  { max: 2, window: 0.05 }, // 휘두르기
  { max: 4, window: 0.05 }, // 타격(브리프: 50ms 안 최대 4개)
  { max: 3, window: 0.06 }, // 처치
  { max: 1, window: 0.1 }, // 몹 등장(0.12초 간격 줄지어 들어옴)
  { max: 2, window: 0.12 }, // 몹 공격
  { max: 1, window: 0.1 }, // 영웅 피격
  { max: 2, window: 0.25 }, // 큰 소리(대량 처치·각성·보스)
  { max: 2, window: 0.05 }, // UI
  { max: 4, window: 0.05 }, // 기타
  { max: 1, window: 0.2 }, // 마무리타 저역 층(다른 묶음 자리를 쓰지 않는다)
];

export interface SfxDef {
  /** 파일 대비 음량(dB). 파일은 모두 피크 -1dBFS로 맞춰 두었다 */
  readonly gainDb: number;
  readonly group: number;
  /** 피치 ±6% 무작위(AV-SPEC). 음악 징글·UI는 끈다 */
  readonly jitter: boolean;
  /** 영웅 기준 좌우 패닝·거리 감쇠 */
  readonly positional: boolean;
}

const d = (gainDb: number, group: number, jitter = true, positional = true): SfxDef => ({ gainDb, group, jitter, positional });
const G = Group;

export const SFX_DEFS: Readonly<Record<SfxName, SfxDef>> = {
  // 2026-10-20 공격 효과음 재생성(저역이 두꺼운 소리): 휘두름 0·1타와 일반 타격을 1~1.5dB 올려 타격감을 앞에 둔다
  swing0: d(-5, G.Swing), swing1: d(-5, G.Swing), swing2: d(-5, G.Swing), swing3: d(-3, G.Swing), swingSkill: d(-8, G.Swing),
  hit0: d(-4, G.Hit), hit1: d(-4, G.Hit), hit2: d(-4, G.Hit), hitCrit: d(-2, G.Hit), hitFinisher: d(0, G.Hit),
  hitSkill: d(-3, G.Hit), hitLight: d(-9, G.Hit), hitBoss: d(-3, G.Hit), blocked: d(-3, G.Hit), heroHurt: d(-2, G.Hurt, true, false),
  kill: d(-6, G.Kill), killBig: d(-3, G.Kill), massKill: d(-1, G.Big, false, false),
  praise1: d(-8, G.Misc, false, false), praise2: d(-7, G.Misc, false, false), praise3: d(-6, G.Misc, false, false), praise4: d(-5, G.Misc, false, false),
  skill1: d(-3, G.Misc, true, false), skill2: d(-3, G.Misc, true, false), skill3: d(-4, G.Misc, true, false), skill3Land: d(-1, G.Big, true, false),
  dodge: d(-7, G.Misc, true, false),
  awakenGather: d(-3, G.Big, false, false), awakenFlash: d(0, G.Big, false, false), awakenSlash: d(-4, G.Hit, false, false),
  mobAttack: d(-15, G.MobAttack), mobSpawn: d(-17, G.Spawn), teleFire: d(-3, G.Big), bossIntro: d(-1, G.Big, false, false), bossDefeated: d(-1, G.Big, false, false),
  waveStart: d(-7, G.Misc, false, false), roomClear: d(-5, G.Misc, false, false), gateOpen: d(-6, G.Misc, false, false), gateClose: d(-7, G.Misc, false, false),
  stageClear: d(-3, G.Misc, false, false), defeat: d(-3, G.Misc, false, false),
  ventWarn: d(-10, G.Misc, false), ventErupt: d(-6, G.Misc), lavaBurn: d(-14, G.Misc),
  shamanCast: d(-10, G.Misc, false), shamanMortar: d(-7, G.Misc),
  emberIntro: d(-2, G.Big, false, false), emberDefeated: d(-2, G.Big, false, false),
  emberNova: d(-6, G.Big, false), emberMeteor: d(-7, G.Big, false),
  emberFissure: d(-7, G.Big, false), emberSummon: d(-8, G.Big, false),
  r2Ambience: d(-25, G.Misc, false, false), villageGateEmber: d(-21, G.Misc, false),
  linkFinish: d(-1, G.Big, false, false),
  skill4Cast: d(-3, G.Misc, true, false), skill4Wave: d(-5, G.Misc, true),
  burstWake: d(-3, G.Big), burstStormEye: d(-2, G.Big, true, false), burstAftershock: d(-2, G.Big),
  burstBloom: d(-1, G.Big, false, false), burstThunder: d(-3, G.Big),
  // 레벨업은 징글이라 피치 흔들림 없이, 전투 소리 위에 들리게
  levelUp: d(-2, G.Big, false, false), skillLearn: d(-5, G.Ui, false, false), skillReset: d(-6, G.Ui, false, false),
  uiClick: d(-8, G.Ui, false, false), uiConfirm: d(-7, G.Ui, false, false), cardPick: d(-6, G.Ui, false, false),
};

/** 번호 순 정의(재생 경로에서 문자열 조회를 피한다) */
export const SFX_BY_ID: readonly SfxDef[] = SFX_NAMES.map((n) => SFX_DEFS[n]);
